import { signal } from '@preact/signals';
import * as A from 'astronomy-engine';
import type { Place } from '../astro/frame';
import { HIJRI_MONTHS, type HijriMonth, type HijriNow, type Starts, daysBetween, gregOf, hijriAt, isCrescentDay, monthOfKey, nextMonth, saudiDate, sunsetOn } from '../astro/hijri';
import { addDays, clockAt } from '../lib/localtime';
import { loadJSON, saveJSON } from '../state';

// Saudi Arabia's Hijri date on the phone: Umm al-Qurá, put right by the month starts Saudi Arabia has decided, which the
// server reads from its announcements (server/watch.ts, GET /api/hijri). The last ones heard are kept on the phone, so
// the date is right offline too. Nothing here is ever shown as a source or a guess: the date is simply the date.

/** The months Saudi Arabia began a day after Umm al-Qurá from Muḥarram 1443 to Rabīʿ al-Ākhir 1448 (the Ministry of
 *  Justice list, data-src/research-2026-10-10/FINDINGS.md 1.7): known before the server is first asked. */
export const SEED: Starts = { '1443-01': '2021-08-10', '1443-03': '2021-10-08', '1443-11': '2022-06-01', '1446-06': '2024-12-03', '1447-11': '2026-04-19' };

/** Whether a date (YYYY-MM-DD) is one the calendar has: not the 99th month, nor 30 February (which would read as 2 March). */
const realDate = (d: string) => {
  const t = Date.parse(`${d}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === d;
};

/** Starts as sent or saved, checked: a month key, a real date, and no more than a day from Umm al-Qurá's 1st. */
export function cleanStarts(v: unknown): Starts {
  const out: Starts = {};
  if (!v || typeof v !== 'object' || Array.isArray(v)) return out;
  for (const [k, d] of Object.entries(v as Record<string, unknown>)) {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(k) || typeof d !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(d) || !realDate(d)) continue;
    const y = Number(k.slice(0, 4));
    if (y < 1300 || y > 1600 || !(Math.abs(daysBetween(gregOf({ ...monthOfKey(k), day: 1 }), d)) <= 1)) continue;
    out[k] = d;
  }
  return out;
}

/** Saudi Arabia's decided month starts, as last heard (and those known from the start). */
export const saudiStarts = signal<Starts>({ ...SEED, ...cleanStarts(loadJSON<unknown>('saudiStarts', null)) });

let lastHeard = 0;
let asking: Promise<boolean> | null = null;

/** Take the decided starts as the server sent them, and keep them on the phone for offline use. */
export function takeStarts(sent: unknown) {
  const got = cleanStarts(sent);
  lastHeard = Date.now();
  const next = { ...SEED, ...got };
  if (JSON.stringify(next) !== JSON.stringify(saudiStarts.peek())) saudiStarts.value = next;
  saveJSON('saudiStarts', got);
}

/** Ask the server for the decided starts; true if it answered. */
export function refreshSaudi(): Promise<boolean> {
  asking ??= (async () => {
    try {
      const r = await fetch('/api/hijri');
      if (!r.ok) return false;
      takeStarts(((await r.json()) as { starts?: unknown }).starts);
      return true;
    } catch {
      return false;
    } finally {
      asking = null;
    }
  })();
  return asking;
}

const deviceZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const whereOf = (p: Pick<Place, 'lat' | 'lon' | 'timeZone'>) => ({ lat: p.lat, lon: p.lon, tz: p.timeZone ?? deviceZone() });

/** The Hijri date at a moment and place, as Saudi Arabia has it (turning at local sunset). */
export const hijriNow = (t: number, p: Pick<Place, 'lat' | 'lon' | 'timeZone'>, starts = saudiStarts.value): HijriNow => hijriAt(t, whereOf(p), starts);

/**
 * Keep the starts fresh while the app is open: soon after it opens, when it comes back to the screen, every half
 * hour, and every two minutes while the date at `home` waits for an announcement. Returns a function that stops it.
 */
export function watchSaudi(home: () => Pick<Place, 'lat' | 'lon' | 'timeZone'>): () => void {
  const due = () => {
    const held = hijriNow(Date.now(), home(), saudiStarts.peek()).held;
    return Date.now() - lastHeard > (held ? 2 * 60e3 : 30 * 60e3);
  };
  const tick = () => document.visibilityState === 'visible' && due() && void refreshSaudi();
  const first = setTimeout(() => void refreshSaudi(), 2000);
  const every = setInterval(tick, 30e3);
  const back = () => document.visibilityState === 'visible' && (due() || Date.now() - lastHeard > 10 * 60e3) && void refreshSaudi();
  document.addEventListener('visibilitychange', back);
  addEventListener('online', back);
  return () => {
    clearTimeout(first);
    clearInterval(every);
    document.removeEventListener('visibilitychange', back);
    removeEventListener('online', back);
  };
}

/**
 * The evening of the 29th of a month at a place (by Saudi Arabia's calendar), when the crescent of the next month is
 * looked for: its local date, the month looked for, and the sunset. Yesterday's, today's or tomorrow's, if one of those
 * days is a 29th; else null.
 */
export function eveningOf29(t: number, p: Pick<Place, 'lat' | 'lon' | 'timeZone'>, starts = saudiStarts.value): { date: string; month: HijriMonth; sunset: number } | null {
  const w = whereOf(p);
  const today = clockAt(t, w.tz).date;
  for (const d of [addDays(today, -1), today, addDays(today, 1)]) {
    const h = saudiDate(d, starts);
    if (h.day === 29) return { date: d, month: nextMonth(h), sunset: sunsetOn(d, w) };
  }
  return null;
}

/**
 * The 29th, the 30th and the 1st of a month at a place, by Saudi Arabia's calendar (the 29th until its date turns,
 * held or not). On those days the app calls the Moon just "Crescent": its phase name, how much of it is lit and when it
 * rises and sets could seem to say whether the new crescent could be seen, and so contradict Saudi Arabia's date
 * (owner, 14 October 2026; the 30th added 22 October 2026).
 */
export function crescentDay(t: number, p: Pick<Place, 'lat' | 'lon' | 'timeZone'>, starts = saudiStarts.value): boolean {
  return isCrescentDay(hijriNow(t, p, starts).day);
}

/**
 * Through the 29th, and while the date is held after it, the month whose crescent Saudi Arabia looks for, and whether
 * that is tonight (else tomorrow evening: the 29th begins at the sunset before). Nothing is then said of where the Moon
 * is or whether it could be seen, only that Saudi Arabia looks for the crescent (owner, 14 October 2026). Else null.
 */
export function lookingFor(t: number, p: Pick<Place, 'lat' | 'lon' | 'timeZone'>, starts = saudiStarts.value): { month: HijriMonth; tonight: boolean } | null {
  const h = hijriNow(t, p, starts);
  if (h.day !== 29) return null;
  if (h.held) return { month: nextMonth(h), tonight: true };
  const ev = eveningOf29(t, p, starts);
  return ev ? { month: ev.month, tonight: ev.date <= clockAt(t, whereOf(p).tz).date } : null;
}

/** "Tonight Saudi Arabia looks for the crescent of Ramaḍān." (or "Tomorrow evening …"). */
export const lookingText = (l: { month: HijriMonth; tonight: boolean }) =>
  `${l.tonight ? 'Tonight' : 'Tomorrow evening'} Saudi Arabia looks for the crescent of ${HIJRI_MONTHS[l.month.month - 1]}.`;

/** The month whose new crescent might be seen at `t`: in the night after the sunset that ends the 29th (or the 30th), and
 *  on the first three days of the month. */
export function crescentMonthAt(t: number, p: Pick<Place, 'lat' | 'lon' | 'timeZone'>, starts = saudiStarts.value): HijriMonth | null {
  const h = hijriNow(t, p, starts);
  if (h.day <= 3) return { month: h.month, year: h.year };
  if (h.day < 29) return null;
  // Held at the 29th after its sunset; or the 30th, begun at the 29th's sunset, while the Sun is down. (The night before the
  // 29th's own day, which is also the 29th, is not yet the evening of looking.)
  if (h.day === 29) return h.held ? nextMonth(h) : null;
  const obs = new A.Observer(p.lat, p.lon, 0);
  const sun = A.Equator(A.Body.Sun, new Date(t), obs, true, true);
  return A.Horizon(new Date(t), obs, sun.ra, sun.dec, 'normal').altitude < 0 ? nextMonth(h) : null;
}
